import { forwardRef, useState } from 'react'
import type { InputHTMLAttributes } from 'react'
import { EyeInvisibleOutlined, EyeOutlined } from '../icons'
import { cx } from './cx'
import { FieldLabel, FieldMessages, useFieldIds } from './fieldChrome'

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'aria-describedby'> {
  readonly label: string
  /** Visually hide the label (still exposed to assistive tech), same seam as
   * the Textarea primitive's `hideLabel` — for dense contexts where a visible
   * label would be redundant (e.g. a chat composer whose placeholder already
   * describes the field). Never drop the label outright. */
  readonly hideLabel?: boolean
  /** Inline error message (components.md "Input (text)" — red border +
   * message + `aria-invalid`, §3.1 of the UID-60 UX spec). Presence alone
   * drives the error visual/ARIA state; callers own WHEN to show it (blur
   * first pass, then every change once touched-and-invalid once, per §3.1). */
  readonly error?: string | undefined
  readonly helpText?: string | undefined
  /** Visual `*` + `aria-required` + "(required)" in the accessible label
   * text — never the asterisk glyph alone (accessibility-wcag skill). */
  readonly required?: boolean
  /** Obscures the value (CVC, routing/account numbers) behind a labeled
   * show/hide toggle — never a bare, unverifiable `type="password"` (UX spec
   * §4.2 "Masked inputs"). */
  readonly maskable?: boolean
  /** Visually masks the typed characters as dots WITHOUT a reveal toggle and
   * WITHOUT switching to `type="password"` — via CSS `text-security` on a plain
   * text input. Used for sensitive numeric fields (CVC, ACH routing/account)
   * where the design shows no eye toggle but the digits must still not render
   * in cleartext on screen. Purely presentational: the full value stays in the
   * field's state/`value` (needed to tokenize); only the on-screen glyphs are
   * obscured. Keeps `inputMode="numeric"` (numeric keypad) and avoids
   * password-manager autofill semantics. */
  readonly mask?: boolean
}

/**
 * Single-line text input primitive (components.md "Input (text)" —
 * `--control-height-md`/`--radius`, default/focus/error/disabled/read-only
 * states). First real consumer: UID-60 checkout (address/shipping/payment
 * fields). Always a real `<label htmlFor>` — never placeholder-as-label.
 *
 * Forwards its ref to the underlying `<input>` DOM node — UID-60's
 * submit-with-errors focus management (UX spec §3.3) needs a real element to
 * move focus to the first invalid field in document order.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    hideLabel = false,
    error,
    helpText,
    required,
    maskable,
    mask,
    className,
    type = 'text',
    onFocus,
    onBlur,
    ...rest
  },
  ref,
) {
  const { id: inputId, errorId, helpId, describedBy } = useFieldIds(rest.id, { error, helpText })
  const [revealed, setRevealed] = useState(false)
  const [focused, setFocused] = useState(false)

  let resolvedType = type
  if (maskable) {
    resolvedType = revealed ? 'text' : 'password'
  }
  // `mask` obscures the glyphs (CSS text-security) only once the field is NOT
  // focused — the buyer sees cleartext while typing (to verify entry), then it
  // masks to dots on blur. Never switches input type (keeps numeric keypad +
  // no password-manager semantics); the value in state is unchanged.
  const masked = mask === true && !focused

  return (
    <div className={cx('field', className)}>
      <FieldLabel htmlFor={inputId} hideLabel={hideLabel} required={required}>
        {label}
      </FieldLabel>
      <div className="input-control-wrap">
        <input
          {...rest}
          ref={ref}
          id={inputId}
          type={resolvedType}
          className={cx(
            'input-control',
            error && 'input-control-error',
            masked && 'input-control-masked',
          )}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          aria-required={required || undefined}
          onFocus={(event) => {
            setFocused(true)
            onFocus?.(event)
          }}
          onBlur={(event) => {
            setFocused(false)
            onBlur?.(event)
          }}
        />
        {maskable && (
          <button
            type="button"
            className="input-reveal-toggle"
            onClick={() => setRevealed((current) => !current)}
            aria-label={revealed ? `Hide ${label}` : `Show ${label}`}
          >
            {revealed ? (
              <EyeInvisibleOutlined aria-hidden="true" />
            ) : (
              <EyeOutlined aria-hidden="true" />
            )}
          </button>
        )}
      </div>
      <FieldMessages ids={{ errorId, helpId }} error={error} helpText={helpText} />
    </div>
  )
})
